// Modules
import { useEffect, useState } from "react";
import apiClient from "../../../../helpers/apiClient";
import { getIcons } from "../../../../helpers/iconCache";

// Components
import { DishResize } from "../room/resize/dish.layout";

// Types
import { SelectorMediaProperties } from "./selectorMedia.properties";
import { SelectorMediaAppType } from "./selectorMedia.types";
import { useTranslation } from "react-i18next";
import { Id, toast } from "react-toastify";

import './selectorMedia.styles.scss';
import { appEventEmitter } from "../../../../context/EventContext";
import { useElectron } from "../../../../hooks/useElectron";

/**
 * Selector Media Component
 * @param {SelectorMediaProperties} properties
 * @returns {JSX.Element}
 */
export function SelectorMediaComponent(properties: SelectorMediaProperties): JSX.Element {

    // translation function
    const { t } = useTranslation();

    const { host, screenCapture, isElectron } = useElectron();

    // state to avoid multiple clicks in media selector
    const [selected, setSelected] = useState(false)

    /// array of devices
    const [apps, setApp] = useState<SelectorMediaAppType[]>([]);

    // get apps from backend
    const getApps = async () => {
      const groups = properties.groupsData;

      let apps: SelectorMediaAppType[] = [];
      let promises: Promise<void>[] = [];

      if (Array.isArray(groups) && groups.length > 0) {
        groups.forEach((group: any) => {
          group.groups_apps && group.groups_apps.forEach((app: any) => {
            promises.push(
              (async () => {
                if (!host || !screenCapture) return;

                const isAppInstalled = await host.isAppAvailable(app.app.url)

                console.log('APP', app.app.name, 'is installed', isAppInstalled);

                if (!apps.find((a) => a.name === app.app.name)) {
                  let isAppActive = false;

                  const sources = await screenCapture.getSources();
                  sources.forEach((source) => {
                    if (source.name === app.app.selector) {
                      isAppActive = true;
                    }
                  });

                  const appIsOnArray = apps.find((a) => a.name === app.app.name);

                  if (appIsOnArray) {
                    appIsOnArray.active = isAppActive;
                  } else {
                    apps.push({
                      ...app.app,
                      active: isAppActive,
                      isInstalled: isAppInstalled,
                    });
                  }
                }
              })()
            );
          });
        });

        Promise.all(promises).then(async () => {
          // Sort the apps by name
          apps.sort((a, b) => a.name.localeCompare(b.name));

          // Find apps that need icons and aren't in the cache
          const appsNeedingIcons = apps.filter(app => !app.icon && !properties.mediaIconCache[app.id]);

          // If we have apps that need icons, fetch them from IndexedDB/API
          if (appsNeedingIcons.length > 0) {
            const appIds = appsNeedingIcons.map(app => app.id);
            const newIcons = await getIcons(appIds, 'app');

            // Update in-memory icon cache
            properties.setMediaIconCache((prev: Record<string, string>) => ({
              ...prev,
              ...newIcons,
            }));

            // Apply icons to apps from new cache or existing cache
            apps = apps.map(app => ({
              ...app,
              icon: newIcons[app.id] || properties.mediaIconCache[app.id] || app.icon || ''
            }));
          } else {
            // Just apply cached icons
            apps = apps.map(app => ({
              ...app,
              icon: app.icon || properties.mediaIconCache[app.id] || ''
            }));
          }

          setApp(apps);
          properties.setHasMediaBeenFetched(true);
        });
      }
    }

    useEffect(() => {
        getApps();
    }, [properties.groupsData]);

    const connectNativeSource = (path: string, name: string, appName: string, appId: string, manualId?: string) => {

        const toastLoadingId = toast.loading(t('media.selector.wait', { appName: appName }), {
            autoClose: false,
            isLoading: true
        })

        if (host) {

            // capture all screen for apps without window name
            if (name === 'Entire Screen') {

                connectSource(name, 0, toastLoadingId, appName, manualId, () => {
                  host.openProgram(path);
                })

            }

            // capture only app window
            else {
                host.openProgram(path)
                connectSource(name, 0, toastLoadingId, appName, manualId, () => {
                    toast.update(toastLoadingId, {
                        type: toast.TYPE.SUCCESS,
                        render: t('media.selector.success', { appName: appName }),
                        autoClose: 5000,
                        isLoading: false
                    });
                });

            }

        }

    }

    const disconnectNativeSource = async (path: string, name: string, appName: string, appId: string) => {
      host?.closeProgram(path);
      toast(t('media.selector.disconnected', { appName: appName }),{
        type: toast.TYPE.SUCCESS,
        autoClose: 5000,
      });

      const stream = properties.appsBeingUsed.find((m) => m.name === appName);
      const streamId = stream?.stream.id;

      properties.setAppsBeingUsed(properties.appsBeingUsed.filter((m) => m.name !== appName));

      properties.removeStream?.(properties.localScreams.findIndex((m) => m.id === streamId));

      await appEventEmitter.emit('app:stopped', {
        action: 'stop',
        resource: 'app',
        agent: streamId || `${appName}-stream`,
        event_phase: 'end',
        app: appId,
        user: properties.user,
        room: properties.room,
      });
    }

    // connect to native source
    const connectSource = async (name: string, times: number, toastId: Id, appName: string, manualId?: string, callBack?: () => void) => {

        screenCapture?.getSources().then(async (sources) => {

            const app = sources.find((source) => source.name === name);

            if (app) {
                try {

                    // get media stream
                    const media = await navigator.mediaDevices.getUserMedia({
                        audio: false,
                        video: {
                            // @ts-ignore
                            mandatory: {
                                chromeMediaSource: 'desktop',
                                chromeMediaSourceId: app.id,
                                minWidth: 1280,
                                maxWidth: 1920,
                                minHeight: 720,
                                maxHeight: 1080
                            }
                        }
                    })

                    const isOnBeingUsed = properties.appsBeingUsed.find((m) => m.name === appName);

                    if (!isOnBeingUsed) {
                      properties.setAppsBeingUsed([...properties.appsBeingUsed, { name: appName, stream: media }]);
                    }

                    // connect stream
                    properties.addStream?.(media, { type: "med.device" }, { manual_id: manualId || "" });

                    await appEventEmitter.emit('app:started', {
                        action: 'start',
                        resource: 'app',
                        agent: media.id || `${appName}-stream`,
                        event_phase: 'start',
                        app: app.id,
                        user: properties.user,
                        room: properties.room,
                    });

                    callBack?.()

                }
                catch (e) {

                    console.log('error mediaDevices', e)

                }

            }
            else {

                if (times < 60) {
                    // try to found app again (apps sometimes are slow to open)
                    setTimeout(() => {

                        connectSource(name, times + 1, toastId, appName, manualId, function() {
                            toast.update(toastId, {
                                type: toast.TYPE.SUCCESS,
                                render: t('media.selector.success', { appName: appName }),
                                autoClose: 5000,
                                isLoading: false
                            });
                        })

                    }, 1000)
                } else {
                    setSelected(false)
                    toast.update(toastId, {
                        type: toast.TYPE.ERROR,
                        render: t('media.selector.fail', { appName: appName }),
                        autoClose: 5000,
                        isLoading: false
                    });

                    await appEventEmitter.emit('app:failed', {
                        action: 'failed',
                        resource: 'app',
                        agent: `${appName}-stream`,
                        event_phase: 'failed',
                        app: appName,
                        user: properties.user,
                        room: properties.room,
                    });
                }
            }
        })

    }

    const [showModal, setShowModal] = useState(false);
    const [manualContent, setManualContent] = useState({ content: '', name: '' });
    const [appToLaunch, setAppToLaunch] = useState<SelectorMediaAppType | undefined>(undefined);

    const fetchManualContent = async (manualId: string) => {
        const response = await apiClient.get(`/api/manuals/${manualId}`);

        return response.data;
    }

    const launchApp = async (app: SelectorMediaAppType) => {
      if (properties.appsBeingUsed.find(m => m.name === app.name)) {
          disconnectNativeSource(app.url, app.selector, app.name, app.id);
          setSelected(false);
          return;
      } else {
          connectNativeSource(app.url, app.selector, app.name, app.id, app.manual_id);
      }

      new DishResize('DishScenary', 'DishScenary', 'DishVideoComponent').resize();
      properties.setScene?.(undefined);
    };

    const handleAppClick = async (app: SelectorMediaAppType) => {
      if (selected) return;
      setSelected(true);

      if (app.manual_id) {
          // Fetch the manual content based on manual_id
          const manual = await fetchManualContent(app.manual_id);
          properties.setIsShowingHelp(true);

          setManualContent(manual);
          setAppToLaunch(app);
          setShowModal(true);
          return;
      }

      launchApp(app);
    };

    const handleCloseModal = () => {
      setShowModal(false);
      properties.setIsShowingHelp(false);
      if (appToLaunch) {
          launchApp(appToLaunch);
          setAppToLaunch(undefined);
      }
    };

    return <>
        {
          properties.canBeDisplayed && apps.map((app, index) => {
              // hide apps without electron
              if (app.url && !isElectron) return <></>

              return app.isInstalled && <div
                  className="camera"
                  style={{
                    backgroundColor: properties.appsBeingUsed.find(m => m.name === app.name) ? '#b7bffc' : '#fff',
                    borderStyle: 'solid',
                    borderColor: properties.appsBeingUsed.find(m => m.name === app.name) ? '#2f49ff' : '#fff',
                }}
                  key={app.id} onClick={() => handleAppClick(app)}>
                  {
                      // hide add icon if device is used
                      <i className={properties.appsBeingUsed.find(m => m.name === app.name) ? 'las la-times' : 'las la-plus'}></i>
                  }
                  <div className="name">{app.name}</div>
                  <div className="model">{app.model}</div>
                  <div className="image" style={{
                      backgroundImage: app.icon ? `url(${app.icon})` : 'unset',
                      }} />
              </div>
          })
        }
        {
            showModal && <div className='HelpView'>
            <div className='HelpContainer'>
              <div className='Header'>
                <div className='Title'>{manualContent.name}</div>
                <div className="Btn-container">
                  <div className='Close-Btn' onClick={() => handleCloseModal()}>
                    { appToLaunch && properties.appsBeingUsed.find(m => m.name === appToLaunch.name) ? t('media.selector.close-app') : t('media.selector.open-app') }
                  </div>
                  <div className='Close-Btn circle' onClick={() => {
                    setShowModal(false)
                    properties.setIsShowingHelp(false)
                    setAppToLaunch(undefined)
                    properties.setScene?.(undefined)
                  }}>
                    <i className='las la-times'></i>
                  </div>
                </div>
              </div>
              <div className='Body'>
                <div dangerouslySetInnerHTML={{ __html: manualContent.content }} />
              </div>
            </div>
          </div>
        }
    </>
}
